import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Shield, AlertTriangle, Loader2, CheckCircle, XCircle, List } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompliancePanel = () => {
  const [text, setText] = useState('');
  const [docType, setDocType] = useState('invoice');
  const [loading, setLoading] = useState(false);
  const [complianceResult, setComplianceResult] = useState(null);
  const [riskResult, setRiskResult] = useState(null);
  const [rules, setRules] = useState([]);
  const token = localStorage.getItem('reasondms_token');

  useEffect(() => { loadRules(); }, [docType]);

  const loadRules = async () => {
    try {
      const res = await fetch(`${API_URL}/api/enterprise/compliance/rules?doc_type=${docType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { const d = await res.json(); setRules(d.rules || []); }
    } catch (err) { /* silent */ }
  };

  const runValidation = async () => {
    if (!text.trim()) { toast.error('Enter document text'); return; }
    setLoading(true);
    try {
      const [compRes, riskRes] = await Promise.all([
        fetch(`${API_URL}/api/enterprise/compliance/validate`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, doc_type: docType })
        }),
        fetch(`${API_URL}/api/enterprise/risk/score`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, doc_type: docType })
        })
      ]);
      if (compRes.ok) {
        setComplianceResult(await compRes.json());
      } else {
        toast.error(compRes.status === 404 ? 'Compliance service not available' : 'Compliance check failed');
      }
      if (riskRes.ok) {
        setRiskResult(await riskRes.json());
      } else {
        toast.error(riskRes.status === 404 ? 'Risk scoring service not available' : 'Risk scoring failed');
      }
      if (compRes.ok || riskRes.ok) toast.success('Compliance & risk analysis complete');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 space-y-3">
          <textarea
            data-testid="compliance-text-input"
            value={text}
            onChange={e => setText(e.target.value)}
            className="w-full h-28 text-sm p-3 border rounded-lg font-mono resize-y focus:ring-2 focus:ring-blue-300 outline-none"
            placeholder="Paste document text for compliance validation..."
          />
          <div className="flex items-center gap-3">
            <select value={docType} onChange={e => setDocType(e.target.value)}
              data-testid="compliance-doc-type"
              className="text-sm border rounded-lg px-3 py-2 bg-white">
              {['invoice','contract','purchase_order','tax_document','agreement','general'].map(t => (
                <option key={t} value={t}>{t.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>
              ))}
            </select>
            <Button data-testid="compliance-validate-btn" onClick={runValidation} disabled={!text.trim() || loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Shield className="h-4 w-4 mr-2" />}
              Validate Compliance
            </Button>
            <span className="text-xs text-slate-400 ml-auto">{rules.length} rules for {docType}</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Compliance Result */}
        {complianceResult?.success && (
          <Card data-testid="compliance-result-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Shield className="h-4 w-4 text-blue-600" /> Compliance Report</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <span className={`text-2xl font-bold ${complianceResult.compliance_score >= 80 ? 'text-emerald-600' : complianceResult.compliance_score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                    {complianceResult.compliance_score}
                  </span>
                  <p className="text-xs text-slate-400">/100</p>
                </div>
                <Badge className={complianceResult.overall_status === 'COMPLIANT' ? 'bg-emerald-100 text-emerald-700' : complianceResult.overall_status === 'NON_COMPLIANT' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                  {complianceResult.overall_status}
                </Badge>
              </div>
              <div className="flex gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-emerald-500" />{complianceResult.passed} Passed</span>
                <span className="flex items-center gap-1"><XCircle className="h-3 w-3 text-red-500" />{complianceResult.failed} Failed</span>
                <span className="flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-amber-500" />{complianceResult.warnings} Warnings</span>
              </div>
              {complianceResult.violations?.length > 0 && (
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {complianceResult.violations.map((v, i) => (
                    <div key={i} className={`p-2 rounded text-xs flex items-start gap-2 ${v.status === 'FAIL' ? 'bg-red-50' : v.status === 'WARNING' ? 'bg-amber-50' : 'bg-emerald-50'}`}>
                      {v.status === 'PASS' ? <CheckCircle className="h-3 w-3 text-emerald-500 shrink-0 mt-0.5" /> :
                       v.status === 'FAIL' ? <XCircle className="h-3 w-3 text-red-500 shrink-0 mt-0.5" /> :
                       <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0 mt-0.5" />}
                      <div>
                        <span className="font-medium">{v.rule_name}</span>
                        <p className="text-slate-500 mt-0.5">{v.details}</p>
                        {v.recommendation && <p className="text-blue-600 mt-0.5">{v.recommendation}</p>}
                      </div>
                      <Badge variant="outline" className="text-xs ml-auto shrink-0">{v.severity}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Risk Result */}
        {riskResult?.success && (
          <Card data-testid="risk-result-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Risk Report</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <span className={`text-2xl font-bold ${riskResult.risk_score <= 30 ? 'text-emerald-600' : riskResult.risk_score <= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                    {riskResult.risk_score}
                  </span>
                  <p className="text-xs text-slate-400">risk /100</p>
                </div>
                <Badge className={`text-xs ${riskResult.risk_level === 'LOW' ? 'bg-emerald-100 text-emerald-700' : riskResult.risk_level === 'MEDIUM' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                  {riskResult.risk_level}
                </Badge>
              </div>
              <p className="text-xs text-slate-600">{riskResult.risk_summary}</p>
              {riskResult.risk_factors?.length > 0 && (
                <div className="space-y-1">
                  {riskResult.risk_factors.map((f, i) => (
                    <div key={i} className="p-2 bg-slate-50 rounded text-xs">
                      <span className="font-medium capitalize">{f.factor?.replace(/_/g, ' ')}</span>
                      <span className="text-slate-400 ml-2">({f.severity})</span>
                      <p className="text-slate-500 mt-0.5">{f.details}</p>
                    </div>
                  ))}
                </div>
              )}
              {riskResult.mitigation_steps?.length > 0 && (
                <div className="mt-2">
                  <span className="text-xs font-medium text-slate-500">Mitigation:</span>
                  <ul className="list-disc pl-4 mt-1">
                    {riskResult.mitigation_steps.map((s, i) => <li key={i} className="text-xs text-slate-600">{s}</li>)}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Available Rules */}
      {rules.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><List className="h-4 w-4" /> Available Rules for {docType}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {rules.map(r => (
                <div key={r.id} className="flex items-center gap-2 text-xs p-2 bg-slate-50 rounded">
                  <Badge variant="outline" className="text-xs shrink-0">{r.id}</Badge>
                  <span className="text-slate-700">{r.condition}</span>
                  <Badge className={`ml-auto text-xs shrink-0 ${r.severity === 'high' ? 'bg-red-100 text-red-700' : r.severity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{r.severity}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CompliancePanel;
